import { useEffect, useRef, useState } from 'react';
import { InteractionStatus, PromptValue } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import type { ReactNode } from 'react';
import { getApiScopes } from './msalConfig';
import {
  clearSignInAttempted,
  markSignInAttempted,
  markSignInFailed,
  peekSignInAttempted,
  useAuthStatus,
} from './authStatus';
import { SigningInPage } from '../pages/SigningInPage';
import { SignInBlockedPage } from '../pages/SignInBlockedPage';

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Route guard for every authenticated subtree (the customer portal shell and
 * the Site Admin console). An unauthenticated caller is sent **straight to the
 * Microsoft sign-in page** rather than to our `/sign-in` landing: there is no
 * portal-side account creation, sign-up, or invitation flow, so that landing
 * only ever held a button (DDR-PLT-024, WEB PRD §2.2).
 *
 * Four properties this guard must hold, each of which is a real defect if lost:
 *
 *  1. **The redirect lives here, not at the app root.** The five public routes
 *    (`/sign-in`, `/forbidden`, `/oops`, `/approval/:action`, and the 404
 *    catch-all) are declared outside this guard and must stay anonymous — most
 *    critically the emailed magic-link approval page, whose approvers are
 *    explicitly not required to hold a portal session (ADR-018).
 *  2. **Every path out is bounded.** `redirectUri` is `/`, which is *inside*
 *    this guard, so any return-without-a-session lands right back here. Two
 *    distinct cases, and both must dead-end rather than bounce:
 *      - *Cancel at Microsoft* — Entra returns an error, the `main.tsx`
 *        bootstrap catches it and sets `signInFailed`.
 *      - *Back button at Microsoft* — the user returns with **no error and no
 *        session**, which is indistinguishable from a first visit. The
 *        `signInAttempted` tab flag is what tells them apart. Without it the
 *        guard redirects again on every Back press, and no non-Microsoft page
 *        is ever reachable.
 *    Both render the "sign-in didn't complete" card, whose "Try again" is the
 *    way out.
 *  3. **A live session always wins.** `isAuthenticated` is checked first, so a
 *    stale `signInFailed` can never lock out a user who does hold a session —
 *    e.g. someone who cancelled a *re-*authentication from the session-expired
 *    modal still has their original account cached and should keep working.
 *  4. **Session expiry is not routed through here.** An authenticated user
 *    whose silent renewal fails keeps their MSAL account (MSAL evicts only the
 *    refresh token), so this guard never re-evaluates; `SessionExpiredHost`
 *    shows a modal instead and nothing navigates until the user chooses to.
 *    That is what protects a half-filled multi-step request form. This is also
 *    why the guard is hand-rolled rather than built on
 *    `MsalAuthenticationTemplate`: that component calls `acquireTokenSilent` on
 *    every *authenticated* mount and falls back to a full-page `loginRedirect`
 *    on `InteractionRequiredAuthError`, which would reintroduce exactly the
 *    mid-session bounce this guard is required not to cause.
 *
 * `prompt: 'select_account'` is set only here, on the guard-initiated login.
 * The deployed authority is a workforce one with strong ambient SSO, so an
 * automatic redirect would otherwise sign a multi-account user in as an
 * identity they never chose and give them no way to switch. The explicit-click
 * paths (`SignInPage`, `SignInBlockedPage`, `SessionExpiredHost`) deliberately
 * keep the default prompt — there the user has already affirmed intent.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const { signInFailed } = useAuthStatus();

  // Snapshotted at mount: "this tab was already sent to Microsoft". Survives the
  // full page load a Back press causes, which module state and refs do not.
  const [attemptedBefore] = useState(peekSignInAttempted);

  // A Back press can also restore this very page from the back/forward cache,
  // which resumes the live JS heap — no remount, no re-read of the flag above.
  // `pageshow` with `persisted` is the only signal that distinguishes it.
  const [restoredFromBfcache, setRestoredFromBfcache] = useState(false);
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setRestoredFromBfcache(true);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Only start a login once per mount, and never while MSAL is already mid
  // interaction — a second `loginRedirect` would throw `interaction_in_progress`.
  // A ref rather than state so it cannot itself trigger a render, and so it
  // survives StrictMode's double-invoked effect.
  const loginRequested = useRef(false);
  const cameBackWithoutSigningIn = attemptedBefore || restoredFromBfcache;
  const shouldStartLogin =
    !isAuthenticated &&
    !signInFailed &&
    !cameBackWithoutSigningIn &&
    inProgress === InteractionStatus.None;

  useEffect(() => {
    if (isAuthenticated) {
      // A session exists, so the tab is no longer mid-attempt. Clearing here
      // (rather than never) keeps the flag from suppressing a legitimate
      // re-authentication later in the same tab.
      clearSignInAttempted();
      return;
    }
    if (!shouldStartLogin || loginRequested.current) {
      return;
    }
    loginRequested.current = true;
    markSignInAttempted();

    // The returned promise does not resolve on success — the page navigates
    // away. A rejection means the redirect never started (an interaction
    // already in progress, a `postLogoutRedirectUri`/config problem), which is
    // the same dead end the bootstrap reports, so it lands on the same card.
    void instance
      .loginRedirect({ scopes: getApiScopes(), prompt: PromptValue.SELECT_ACCOUNT })
      .catch((error: unknown) => {
        console.error('MSAL sign-in redirect did not start', error);
        markSignInFailed();
      });
  }, [isAuthenticated, shouldStartLogin, instance]);

  // Property 3: a live session outranks every failure signal.
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Property 2: both ways of returning without a session dead-end here rather
  // than bouncing. `inProgress` keeps the in-flight redirect showing the
  // transitional card instead of flashing a failure the user hasn't hit yet.
  if (signInFailed || (cameBackWithoutSigningIn && inProgress === InteractionStatus.None)) {
    return <SignInBlockedPage reason="signInFailed" />;
  }

  // Redirecting to Microsoft. Reuse the same neutral transitional card the
  // post-token wait uses so the hand-off reads as one continuous "signing you
  // in" rather than a blank frame.
  return <SigningInPage />;
}
